import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import routes from '../../../../../../../Routes/routes';
import {
    DEFAULT_LEFT,
    DRAG_HANDLE_WIDTH,
    SEARCH_MODEL
} from '../../../../../../../components/table/Model/TableModel';
import CustomTh from '../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../components/table/CustomTd';
import {
    CloneIconOptions,
    DeleteIconsOptions, DetailsIconsOptions,
    UpdateIconsOptions,
} from '../../../../../../../components/buttons/IconButtons';
import DropDownMenuProfile from '../../../../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../../../../components/DropDown/DropDownMenuItem';
import { SimpleSearch } from '../../../../../../../reduxToolkit/Search/SearchApi';
import TableFunction from '../../../../../../../components/table/TableFunction';
import {EMPTY_VALUES, errorMsg} from '../../../../../../../Apis/functions';
import { permissionObj } from '../../../../../../../Helpers/permission';
import { CustomSwitch } from '../../../../../../../components/CustomSwitch';
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import {DeleteItemsApi} from "../../../../../../../reduxToolkit/Commons/CommonsApi";
import {TABLES} from "../../../../../../../Apis/Tables";
import {
    UpdatePriorityPayrollPolicy,
    UpdateStatusPayrollPolicy
} from "../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi";
import {toast} from "react-toastify";
import DragHandleCell from '../../../../../../../components/table/DragHandleCell';
import {getCalculationTypeById, POLICY_CONDITION_MODEL} from "../CreateUpdatePolicy/Model/Model";
import ScopeCell from "../CreateUpdatePolicy/SubComponents/ScopeCell";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Logic hook for the global overtime policies table.
 * @param {string} searchElement - Active search term.
 * @param {Function} setBodyForExport - Setter for the export request body.
 * @param {Object} option - Search model options.
 * @param {boolean} modified - Flag indicating table data has changed.
 * @param {Function} setModified - Setter for the modified flag.
 * @param {Function} registerAction - Registers tab-level actions (e.g. create button) externally.
 * @param {number} activeStep - Current stepper step index, forwarded to the create navigation state.
 * @param {string} subView - Identifier of the current sub-view tab.
 * @param selectForDelete
 * @param setSelectForDelete
 * @returns {Object} State and handlers for the global table component.
 */
const GlobalTableApp = ({
    searchElement,
    setBodyForExport,
    option,
    modified,
    setModified,
    registerAction,
    activeStep,
    subView,
    selectForDelete,
    setSelectForDelete,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const tableId = 'tbl_gt_global_overtime';
    const searchId = 'txt_gt_global_overtime';

    const [results, setResults] = useState({content: [], totalElements: 0});
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_UPDATE);
    const canDelete = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_DELETE);
    const canClone = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_CLONE);
    const canCreate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_CREATE);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);
    const leftPosition = DEFAULT_LEFT;
    // Width of the drag handle column; the "#" column sticks right after it.
    const firstColumnLeft = canUpdate ? DRAG_HANDLE_WIDTH : leftPosition;
    // Snug width for the index ("#") column so the name column sticks right after it
    // without leaving empty space inside an oversized cell.
    const firstColumnWidth = 50;
    const secondColumnLeft = firstColumnLeft + firstColumnWidth;
    /**
     * Opens the delete confirmation modal for the toolbar bulk-delete action.
     */
    const handleDelete = () => {
        setDeleteModalShow(true);
    };

    /**
     * Registers handleCreate and handleDelete with the parent tab.
     * Re-registers on selection change so the closure sees the latest selectForDelete.
     */
    useEffect(() => {
        registerAction(subView, {handleCreate, handleDelete});
    }, [selectForDelete]);

    /**
     * Dispatches a priority update for a single policy after a successful drag-and-drop.
     * @param {number|string} id - ID of the policy being reordered.
     * @param {number} newIndex - Zero-based target index in the list.
     */
    const handleUpdatePriority = ({ id, newIndex }) => {
        dispatch(UpdatePriorityPayrollPolicy({ id, priority: parseInt(newIndex + 1) }))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    setModified(true);
                    toast.success(t('has_been_updated_successfully'));
                } else {
                    errorMsg(code);
                }
            });
    };

    /**
     * Handles the end of a drag-and-drop sort event.
     * Computes the new index from the drop target and delegates to handleUpdatePriority.
     * @param {Object} event - DndKit drag end event containing active and over descriptors.
     */
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const newIndex = results.content.findIndex(i => i.id === over.id);
        handleUpdatePriority({ id: active.id, newIndex });
    };

    const handleStatusChange = () => {
        let successMsg;
        let statusId;

        if (selectedItem.statusId === STATUS_MODEL.ACTIVE_COMPANY) {
            statusId = STATUS_MODEL.IN_ACTIVE_COMPANY;
            successMsg = "has_been_deactivate_successfully";

        } else {
            statusId = STATUS_MODEL.ACTIVE_COMPANY;
            successMsg = "has_been_activate_successfully";
        }

        dispatch(UpdateStatusPayrollPolicy({id: selectedItem.id, statusId})).then(
            ({payload: {data: {success, code}}}) => {
                setShowActiveInactiveModal(false);
                setSelectedItem(null);
                if (success === true) {
                    toast.success(t(successMsg));
                    setModified(true);
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    /**
     * Navigates to the create policy form, passing back-navigation context and scope flags.
     */
    const handleCreate = () => {
        navigate(routes.overtimePolicies.create,
            {
                state: {
                    backUrl: routes.attendance.main,
                    currentTab: activeStep,
                    currentSubView: subView,
                    isGlobal: true
                }
            });
    };

    /**
     * Selects an item for deletion via the row menu and opens the confirmation modal.
     * @param {Object} item - The policy row to delete.
     */
    const handleDeleteItem = (item) => {
        setSelectedItem(item);
        setSelectForDelete([item.id]);
        setDeleteModalShow(true);
    };

    /**
     * Closes the delete confirmation modal and clears the selection.
     */
    const handleDeleteModalClose = () => {
        setDeleteModalShow(false);
        setSelectedItem(null);
        setSelectForDelete([]);
    };

    /**
     * Confirms deletion of selected items and triggers a table refresh.
     */
    const handleDeleteSelected = () => {
        dispatch(DeleteItemsApi({tableName: TABLES.PAYROLL_POLICY, body: selectForDelete})).then(
            ({
                 payload: {
                     data: {success, code},
                 },
             }) => {
                if (success === true) {
                    handleDeleteModalClose();
                    setModified(true);
                } else errorMsg(code);
            }
        );
    };

    /**
     * Builds the table header row with translated column labels.
     * @returns {JSX.Element[]} Array of CustomTh header cells.
     */
    const getThead = () => [
        canUpdate && <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head" customWidth={DRAG_HANDLE_WIDTH}/>,
        <CustomTh tableId={tableId} fixedXY={true} left={firstColumnLeft} classes="first_head" customWidth={firstColumnWidth}>#</CustomTh>,
        <CustomTh tableId={tableId} fixedXY={true} left={secondColumnLeft}>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('condition')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('calculation_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('calculation_value')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('scope')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
    ];

    /**
     * Builds the table body rows from the current results.
     * Returns empty arrays when there is no data.
     * @returns {{body: JSX.Element[], data: Object[]}} Rendered rows and raw data.
     */
    const getTbody = () => {
        if (!results?.content?.length) return {body: [], data: []};

        const body = results.content.map((row, index) => {
            const trigger = POLICY_CONDITION_MODEL.values().find(v => v.key === row.condition);
            const conditionLabel = trigger ? t(trigger.trans) : (row.condition || EMPTY_VALUES.DEFAULT);
            const calculationType = getCalculationTypeById(row.calculationType);
            const calculationTypeTrans = calculationType?.name;
            const calculationTypePrefixText = calculationType?.prefixText;
            const weekly = row.payrollPolicyWeeklyScope
                ? {
                    dayArray: row.payrollPolicyWeeklyScope.map(e => e.dayId),
                    startTime: row.payrollPolicyWeeklyScope[0]?.startTime,
                    endTime: row.payrollPolicyWeeklyScope[0]?.endTime,
                }
                : {};
            const date   = row.payrollPolicyDateScope  ?? {};
            const hour   = row.payrollPolicyHourScope  ?? {};
            const bonus  = row.payrollPolicyBonusScope  ?? {};
            const scopes = { condition: row.condition, ...weekly, ...date, ...hour, ...bonus };

            return <>
                { canUpdate && <DragHandleCell tableId={tableId} fixed={true} left={leftPosition} /> }
                <CustomTd tableId={tableId} fixed={true} left={firstColumnLeft} classes="first-column">
                    {index + 1}
                </CustomTd>
                <CustomTd tableId={tableId} fixed={true} left={secondColumnLeft}>
                    {row?.name ?? EMPTY_VALUES.DEFAULT}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={conditionLabel}
                        variant='compact'
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.shades100}
                        fontColor={colors.shades700}
                        borderColor={colors.shades150}
                        legend={calculationTypeTrans}
                        variant='large'
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.shades100}
                        fontColor={colors.shades700}
                        borderColor={colors.shades150}
                        legend={`${calculationTypePrefixText} ${row.calculationValue}`}
                        variant='large'
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <ScopeCell rule={scopes} t={t}/>
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomSwitch
                        id={`cb_gt_status_${row?.id}`}
                        name="status"
                        checked={row?.statusId === STATUS_MODEL.ACTIVE_COMPANY}
                        showBorder={false}
                        className=""
                        disabled={!canUpdate}
                        onChange={() => {
                            setSelectedItem(row);
                            setShowActiveInactiveModal(true);
                        }}
                    />
                </CustomTd>
            </>
        });

        return {body, data: results.content};
    };


    /**
     * Renders the actions dropdown menu (update, delete, clone) for a table row.
     * @param {Object} row - The row data.
     * @param {number} index - Row index within the table.
     * @returns {JSX.Element} Dropdown menu with available actions.
     */
    const getActionsButtons = (row, index) => (
        <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-gt-${row?.id}`}
        >
            {canUpdate && (
                <DropDownMenuItem
                    id={`btn_gt_update_${row?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    click={() => navigate(`${routes.overtimePolicies.update}`,
                        { state: {
                            id: row?.id,
                            isGlobal: true,
                            backUrl: routes.attendance.main,
                            currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                            currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL
                        } })}
                    icon={<UpdateIconsOptions />}
                />
            )}

            {!canUpdate && <DropDownMenuItem
                id={`btn_gt_details_${row?.id}`}
                index={index}
                handleClose={handleClose}
                name={t('details')}
                click={() => navigate(`${routes.overtimePolicies.details}`,
                    { state: {
                            id: row?.id,
                            isGlobal: true,
                            backUrl: routes.attendance.main,
                            currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                            currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL
                        } })}
                icon={<DetailsIconsOptions />}
            />
            }

            {canClone && (
                <DropDownMenuItem
                    id={`btn_gt_clone_${row?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('clone')}
                    click={() => navigate(`${routes.overtimePolicies.clone}`,
                        { state: {
                                id: row?.id,
                                isGlobal: true,
                                backUrl: routes.attendance.main,
                                currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                                currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.GLOBAL
                            } })}
                    icon={<CloneIconOptions />}
                />
            )}
            {canDelete && (
                <DropDownMenuItem
                    id={`btn_gt_delete_${row?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('delete')}
                    click={() => handleDeleteItem(row)}
                    icon={<DeleteIconsOptions />}
                    fontColor={'font-color-error-600'}
                    iconColor={'icon-color-error-code-600'}
                />
            )}
        </DropDownMenuProfile>
    );

    /**
     * Fetches a page of data from the server and updates the export body.
     * @param {number} page - Page number to load.
     * @param {Object} params - Additional search parameters.
     * @param {string} params.searchElement - Search term filter.
     */
    const getData = (page, { searchElement }) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            key: searchElement,
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) setResults(data);
        });
    };

    const { thead, tbody, t, pagination, rowsPerPage, openMenu, handleMenuToggle, handleClose, handleCheckboxChange, handleDeleteAll } = TableFunction({
        getTbody,
        getThead,
        data: results,
        labelRowsPerPage: 'rules_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement,
        getData,
        modified,
        setModified,
        selectedList: selectForDelete,
        setSelectedList: setSelectForDelete
    });

    return {
        t,
        DEV,
        permission,
        results,
        setResults,
        rowsPerPage,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        getActionsButtons,
        deleteModalShow,
        selectedItem,
        handleDeleteModalClose,
        handleDeleteSelected,
        handleCreate,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        handleStatusChange,
        handleDragEnd,
        canUpdate,
        canCreate
    };
};

export default GlobalTableApp;
